import {useQuery} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useExpedientes, type Expediente} from '@/hooks/sgsst/evaluacion/use-sgsst'
import type {components} from '@/lib/api-schema'

// Las filas del snapshot viajan como `Record<string, unknown>[]` (dict congelado); cada vista
// de solo lectura tipa su forma local.
export type ExpedienteMatrizRiesgos = components['schemas']['ExpedienteMatrizRiesgosRead']
export type ExpedienteMatrizLegal = components['schemas']['ExpedienteMatrizLegalRead']
export type ExpedienteBateria = components['schemas']['ExpedienteBateriaRead']

export function useExpedienteEntry(businessId: string | undefined, year: number) {
  const canOperate = useCapabilities(businessId).sgsst_operar
  const {data: expedientes, isLoading} = useExpedientes(canOperate ? businessId : undefined)
  const exp: Expediente | undefined = expedientes?.find((e) => e.period_year === year)
  return {canOperate, isLoading: canOperate && isLoading, exp}
}

/** Del snapshot que congeló el cierre, nunca la versión viva. */
export function useExpedienteMatrizRiesgos(businessId: string | undefined, year: number, enabled: boolean) {
  return useQuery({
    queryKey: ['expediente-matriz-riesgos', businessId, year],
    queryFn: async () =>
      (await api.get<ExpedienteMatrizRiesgos>(`/businesses/${businessId}/expediente/${year}/matriz-riesgos`)).data,
    enabled: enabled && !!businessId,
  })
}

export function useExpedienteMatrizLegal(businessId: string | undefined, year: number, enabled: boolean) {
  return useQuery({
    queryKey: ['expediente-matriz-legal', businessId, year],
    queryFn: async () =>
      (await api.get<ExpedienteMatrizLegal>(`/businesses/${businessId}/expediente/${year}/matriz-legal`)).data,
    enabled: enabled && !!businessId,
  })
}

/** Solo agregados de grupo (custodia según Res. 2764/2022 art. 5). */
export function useExpedienteBateria(businessId: string | undefined, year: number, enabled: boolean) {
  return useQuery({
    queryKey: ['expediente-bateria', businessId, year],
    queryFn: async () =>
      (await api.get<ExpedienteBateria>(`/businesses/${businessId}/expediente/${year}/bateria`)).data,
    enabled: enabled && !!businessId,
  })
}
